import { useState, useEffect } from 'react'
import { Phone, PhoneIncoming, PhoneOutgoing, PhoneForwarded, MoreVertical, Plus, Loader2, Clock } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import { useToast } from '@/hooks/use-toast'
import api from '@/lib/api'
import { formatPhoneDisplay, formatPhoneInput, phoneToE164 } from '@/lib/utils'

// --- Types ---

export interface Schedule {
  timezone: string
  hours: Record<string, { open: string; close: string } | null>
}

interface AIMode {
  inboundEnabled: boolean
  outboundEnabled: boolean
  forwardNumber: string | null
  forwardMessage: string
  inboundSchedule: Schedule | null
  outboundSchedule: Schedule | null
}

type AIState = 'on' | 'off' | 'scheduled'

interface PhoneNodeProps {
  phone: {
    id: string
    phoneNumber: string
    provider?: string
    status: string
    assistantId?: string
    outboundAssistantId?: string
  }
  inboundAgents: { id: string; name: string }[]
  outboundAgents: { id: string; name: string }[]
  assignedInboundName?: string
  assignedOutboundName?: string
  onInboundChange: (phoneId: string, agentId: string) => void
  onOutboundChange: (phoneId: string, agentId: string) => void
  onDelete: (phoneId: string, phoneNumber: string) => void
  assigningInbound?: boolean
  assigningOutbound?: boolean
}

// --- Constants ---

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const
const DAY_LABELS: Record<string, string> = {
  monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu',
  friday: 'Fri', saturday: 'Sat', sunday: 'Sun',
}

export const DEFAULT_SCHEDULE: Schedule = {
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  hours: {
    monday: { open: '09:00', close: '17:00' },
    tuesday: { open: '09:00', close: '17:00' },
    wednesday: { open: '09:00', close: '17:00' },
    thursday: { open: '09:00', close: '17:00' },
    friday: { open: '09:00', close: '17:00' },
    saturday: null,
    sunday: null,
  }
}

const COMMON_TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Phoenix',
  'America/Anchorage',
  'Pacific/Honolulu',
]

const TZ_LABELS: Record<string, string> = {
  'America/New_York': 'Eastern',
  'America/Chicago': 'Central',
  'America/Denver': 'Mountain',
  'America/Los_Angeles': 'Pacific',
  'America/Phoenix': 'Arizona',
  'America/Anchorage': 'Alaska',
  'Pacific/Honolulu': 'Hawaii',
}

function formatTime12(t: string) {
  const [h, m] = t.split(':').map(Number)
  const ampm = h >= 12 ? 'PM' : 'AM'
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h
  return `${h12}:${m.toString().padStart(2, '0')} ${ampm}`
}

/** Convert 24h string "HH:MM" to { hour12, minute, ampm } */
function to12h(t: string) {
  const [h, m] = t.split(':').map(Number)
  return {
    hour12: h === 0 ? 12 : h > 12 ? h - 12 : h,
    minute: m,
    ampm: h >= 12 ? 'PM' : 'AM',
  }
}

/** Convert 12h parts back to 24h "HH:MM" string */
function to24h(hour12: number, minute: number, ampm: string): string {
  let h = hour12
  if (ampm === 'AM' && h === 12) h = 0
  else if (ampm === 'PM' && h !== 12) h += 12
  return `${String(h).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
}

/** Compact 12-hour time picker */
function TimePicker12({ value, onChange, error }: { value: string; onChange: (v: string) => void; error?: boolean }) {
  const { hour12, minute, ampm } = to12h(value)

  const update = (h12: number, m: number, ap: string) => {
    onChange(to24h(h12, m, ap))
  }

  return (
    <div className={`inline-flex items-center gap-0.5 h-6 rounded border px-1 ${error ? 'border-red-400' : 'border-border'} bg-background`}>
      <select
        value={hour12}
        onChange={(e) => update(Number(e.target.value), minute, ampm)}
        className="bg-transparent text-[10px] tabular-nums appearance-none cursor-pointer outline-none w-6 text-center"
      >
        {[12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map(h => (
          <option key={h} value={h}>{h}</option>
        ))}
      </select>
      <span className="text-[10px] text-muted-foreground">:</span>
      <select
        value={minute}
        onChange={(e) => update(hour12, Number(e.target.value), ampm)}
        className="bg-transparent text-[10px] tabular-nums appearance-none cursor-pointer outline-none w-5 text-center"
      >
        {[0, 15, 30, 45].map(m => (
          <option key={m} value={m}>{String(m).padStart(2, '0')}</option>
        ))}
      </select>
      <select
        value={ampm}
        onChange={(e) => update(hour12, minute, e.target.value)}
        className="bg-transparent text-[10px] font-medium appearance-none cursor-pointer outline-none w-7 text-center"
      >
        <option value="AM">AM</option>
        <option value="PM">PM</option>
      </select>
    </div>
  )
}

function deriveState(enabled: boolean, schedule: Schedule | null): AIState {
  if (schedule) return 'scheduled'
  return enabled ? 'on' : 'off'
}

/** Client-side check: is the current time within a schedule's active hours? */
function isWithinScheduleNow(schedule: Schedule): boolean {
  try {
    const now = new Date()
    const weekday = now.toLocaleDateString('en-US', { timeZone: schedule.timezone, weekday: 'long' }).toLowerCase()
    const timeStr = now.toLocaleTimeString('en-GB', { timeZone: schedule.timezone, hour: '2-digit', minute: '2-digit', hour12: false })
    const [h, m] = timeStr.split(':').map(Number)
    const currentMin = (h % 24) * 60 + m

    const day = schedule.hours[weekday]
    if (!day) return false

    const [oH, oM] = day.open.split(':').map(Number)
    const [cH, cM] = day.close.split(':').map(Number)
    return currentMin >= oH * 60 + oM && currentMin < cH * 60 + cM
  } catch {
    return true // fail open
  }
}

// --- Inline Schedule Editor ---

export function ScheduleEditor({
  schedule,
  onChange,
  onSave,
  onCancel,
  saving,
}: {
  schedule: Schedule
  onChange: (s: Schedule) => void
  onSave: () => void
  onCancel: () => void
  saving: boolean
}) {
  const toggleDay = (day: string) => {
    const current = schedule.hours[day]
    onChange({
      ...schedule,
      hours: {
        ...schedule.hours,
        [day]: current ? null : { open: '09:00', close: '17:00' },
      }
    })
  }

  const updateTime = (day: string, field: 'open' | 'close', value: string) => {
    const current = schedule.hours[day]
    if (!current) return
    const updated = { ...current, [field]: value }
    // Auto-fix: if close <= open, nudge close to open + 1 hour
    if (field === 'open' && updated.close <= updated.open) {
      const [h] = updated.open.split(':').map(Number)
      updated.close = `${String(Math.min(h + 1, 23)).padStart(2, '0')}:00`
    }
    onChange({
      ...schedule,
      hours: {
        ...schedule.hours,
        [day]: updated,
      }
    })
  }

  const applyPreset = (preset: 'weekdays' | '24-7') => {
    const hours: Record<string, { open: string; close: string } | null> = {}
    for (const day of DAYS) {
      if (preset === '24-7') {
        hours[day] = { open: '00:00', close: '23:59' }
      } else {
        hours[day] = ['saturday', 'sunday'].includes(day) ? null : { open: '09:00', close: '17:00' }
      }
    }
    onChange({ ...schedule, hours })
  }

  return (
    <div className="space-y-2.5">
      {/* Timezone */}
      <div className="flex items-center gap-2">
        <Clock className="h-3 w-3 text-muted-foreground flex-shrink-0" />
        <Select value={schedule.timezone} onValueChange={(tz) => onChange({ ...schedule, timezone: tz })}>
          <SelectTrigger className="h-7 text-[11px] flex-1">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {COMMON_TIMEZONES.map(tz => (
              <SelectItem key={tz} value={tz} className="text-xs">
                {TZ_LABELS[tz] || tz}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Presets */}
      <div className="flex gap-1.5">
        <button
          onClick={() => applyPreset('weekdays')}
          className="text-[10px] px-2 py-0.5 rounded bg-muted hover:bg-muted/80 text-muted-foreground transition-colors"
        >
          9-5 Weekdays
        </button>
        <button
          onClick={() => applyPreset('24-7')}
          className="text-[10px] px-2 py-0.5 rounded bg-muted hover:bg-muted/80 text-muted-foreground transition-colors"
        >
          24/7
        </button>
      </div>

      {/* Day rows */}
      <div className="space-y-1">
        {DAYS.map(day => {
          const dayHours = schedule.hours[day]
          const isOn = dayHours !== null
          return (
            <div key={day} className="flex items-center gap-1.5">
              <button
                onClick={() => toggleDay(day)}
                className={`w-9 text-[10px] font-medium py-0.5 rounded text-center transition-colors ${
                  isOn
                    ? 'bg-brand-royal/10 text-brand-royal dark:bg-brand-royal/20'
                    : 'bg-muted/60 text-muted-foreground line-through'
                }`}
              >
                {DAY_LABELS[day]}
              </button>
              {isOn ? (
                <>
                  <TimePicker12
                    value={dayHours.open}
                    onChange={(v) => updateTime(day, 'open', v)}
                  />
                  <span className="text-[10px] text-muted-foreground">–</span>
                  <TimePicker12
                    value={dayHours.close}
                    onChange={(v) => updateTime(day, 'close', v)}
                    error={dayHours.close <= dayHours.open}
                  />
                  {dayHours.close <= dayHours.open && (
                    <span className="text-[9px] text-red-500">!</span>
                  )}
                </>
              ) : (
                <span className="text-[10px] text-muted-foreground italic">Off</span>
              )}
            </div>
          )
        })}
      </div>

      {/* Save / Cancel */}
      <div className="flex gap-1.5 pt-1">
        <Button size="sm" className="h-6 text-[11px] flex-1" onClick={onSave} disabled={saving}>
          {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Save Schedule'}
        </Button>
        <Button size="sm" variant="ghost" className="h-6 text-[11px]" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
      </div>
    </div>
  )
}

// --- Schedule Summary (compact display) ---

function ScheduleSummary({ schedule, onClick, isActiveNow }: { schedule: Schedule; onClick: () => void; isActiveNow: boolean | null }) {
  const activeDays = DAYS.filter(d => schedule.hours[d] !== null)
  const allSameTime = activeDays.length > 0 && activeDays.every(d => {
    const h = schedule.hours[d]
    const first = schedule.hours[activeDays[0]]
    return h && first && h.open === first.open && h.close === first.close
  })

  let summary: string
  if (activeDays.length === 0) {
    summary = 'No days scheduled'
  } else if (activeDays.length === 7 && allSameTime) {
    const h = schedule.hours[activeDays[0]]!
    summary = h.open === '00:00' && h.close === '23:59' ? '24/7' : `Every day ${formatTime12(h.open)}–${formatTime12(h.close)}`
  } else if (allSameTime) {
    const h = schedule.hours[activeDays[0]]!
    const dayStr = activeDays.map(d => DAY_LABELS[d]).join(', ')
    summary = `${dayStr} ${formatTime12(h.open)}–${formatTime12(h.close)}`
  } else {
    summary = `${activeDays.length} days · ${TZ_LABELS[schedule.timezone] || schedule.timezone}`
  }

  return (
    <div className="mt-2 px-2 py-1.5 rounded-md border border-border/60 bg-muted/30 flex items-center justify-between gap-2">
      {/* Live status indicator — primary signal */}
      {isActiveNow !== null && (
        <div className="flex items-center gap-1.5">
          <span className={`w-2 h-2 rounded-full ${isActiveNow ? 'bg-green-500 animate-pulse' : 'bg-amber-500'}`} />
          <span className={`text-xs font-medium ${isActiveNow ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400'}`}>
            {isActiveNow ? 'AI active' : 'Forwarding'}
          </span>
        </div>
      )}
      <button onClick={onClick} className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1 hover:underline">
        <Clock className="h-3 w-3" />
        <span>{summary}</span>
        <span className="text-[10px] opacity-70">(edit)</span>
      </button>
    </div>
  )
}

// --- Main Component ---

export default function PhoneNode({
  phone,
  inboundAgents,
  outboundAgents,
  assignedInboundName,
  assignedOutboundName,
  onInboundChange,
  onOutboundChange,
  onDelete,
  assigningInbound,
  assigningOutbound,
}: PhoneNodeProps) {
  const { toast } = useToast()
  const isActive = phone.status === 'active'
  const providerLabel = phone.provider === 'telnyx' ? 'Voice' : 'Legacy'

  // AI mode state
  const [aiMode, setAiMode] = useState<AIMode | null>(null)
  const [aiLoading, setAiLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [forwardInput, setForwardInput] = useState('')
  const [showForwardConfig, setShowForwardConfig] = useState(false)
  const [savingForward, setSavingForward] = useState(false)

  // Schedule editing state
  const [editingInbound, setEditingInbound] = useState(false)
  const [editingOutbound, setEditingOutbound] = useState(false)
  const [draftInboundSchedule, setDraftInboundSchedule] = useState<Schedule>(DEFAULT_SCHEDULE)

  // Live schedule status — refreshes every 60s
  const [liveActive, setLiveActive] = useState<{ inbound: boolean | null; outbound: boolean | null }>({ inbound: null, outbound: null })
  useEffect(() => {
    const check = () => {
      setLiveActive({
        inbound: aiMode?.inboundSchedule ? isWithinScheduleNow(aiMode.inboundSchedule) : null,
        outbound: aiMode?.outboundSchedule ? isWithinScheduleNow(aiMode.outboundSchedule) : null,
      })
    }
    check()
    const interval = setInterval(check, 60_000)
    return () => clearInterval(interval)
  }, [aiMode?.inboundSchedule, aiMode?.outboundSchedule])
  const [draftOutboundSchedule, setDraftOutboundSchedule] = useState<Schedule>(DEFAULT_SCHEDULE)

  useEffect(() => {
    if (isActive) loadAIMode()
  }, [phone.id])

  const loadAIMode = async () => {
    setAiLoading(true)
    try {
      const res = await api.get(`/api/voice/numbers/${phone.id}/ai-mode`)
      if (res.data.success) {
        const mode: AIMode = {
          ...res.data.aiMode,
          inboundSchedule: res.data.aiMode.inboundSchedule || null,
          outboundSchedule: res.data.aiMode.outboundSchedule || null,
        }
        setAiMode(mode)
        setForwardInput(mode.forwardNumber ? formatPhoneInput(mode.forwardNumber) : '')
        if (mode.inboundSchedule) setDraftInboundSchedule(mode.inboundSchedule)
        if (mode.outboundSchedule) setDraftOutboundSchedule(mode.outboundSchedule)
      }
    } catch {
      // Silently fail
    } finally {
      setAiLoading(false)
    }
  }

  // Derive current state from aiMode
  const inboundState: AIState = aiMode ? deriveState(aiMode.inboundEnabled, aiMode.inboundSchedule) : 'on'
  const outboundState: AIState = aiMode ? deriveState(aiMode.outboundEnabled, aiMode.outboundSchedule) : 'on'
  const hasForwardNumber = !!aiMode?.forwardNumber

  // Is AI currently active (for glow color)
  const inboundActive = inboundState === 'on' || inboundState === 'scheduled'
  const outboundActive = outboundState === 'on' || outboundState === 'scheduled'

  const changeMode = async (direction: 'inbound' | 'outbound', newState: AIState) => {
    if (!aiMode) return

    // For 'off' or 'scheduled' inbound, need a forward number
    if (direction === 'inbound' && newState !== 'on' && !forwardInput && !aiMode.forwardNumber) {
      setShowForwardConfig(true)
      toast({ title: 'Enter a forwarding number', description: 'Required for forwarding when AI is off or outside scheduled hours' })
      return
    }

    if (newState === 'scheduled') {
      // Open the schedule editor
      if (direction === 'inbound') {
        setDraftInboundSchedule(aiMode.inboundSchedule || DEFAULT_SCHEDULE)
        setEditingInbound(true)
      } else {
        setDraftOutboundSchedule(aiMode.outboundSchedule || DEFAULT_SCHEDULE)
        setEditingOutbound(true)
      }
      return
    }

    // For 'on' or 'off', save immediately
    setSaving(true)
    try {
      const payload: any = {}
      if (direction === 'inbound') {
        payload.inboundEnabled = newState === 'on'
        payload.inboundSchedule = null // Clear schedule when switching to on/off
        if (newState === 'off' && forwardInput) {
          payload.forwardNumber = phoneToE164(forwardInput)
        }
      } else {
        payload.outboundEnabled = newState === 'on'
        payload.outboundSchedule = null
      }

      const res = await api.put(`/api/voice/numbers/${phone.id}/ai-mode`, payload)
      if (res.data.success) {
        await loadAIMode()
        toast({ title: 'Updated', description: res.data.message })
      }
    } catch (error: any) {
      toast({ title: 'Error', description: error.response?.data?.message || 'Failed to update', variant: 'destructive' })
    } finally {
      setSaving(false)
    }
  }

  const saveSchedule = async (direction: 'inbound' | 'outbound') => {
    const schedule = direction === 'inbound' ? draftInboundSchedule : draftOutboundSchedule

    // Validate: close must be after open for all active days
    for (const day of DAYS) {
      const h = schedule.hours[day]
      if (h && h.close <= h.open) {
        toast({ title: 'Invalid schedule', description: `${DAY_LABELS[day]}: end time must be after start time`, variant: 'destructive' })
        return
      }
    }

    setSaving(true)
    try {
      const payload: any = {}
      if (direction === 'inbound') {
        payload.inboundEnabled = true // Keep enabled; schedule controls timing
        payload.inboundSchedule = schedule
        if (forwardInput) payload.forwardNumber = phoneToE164(forwardInput)
      } else {
        payload.outboundEnabled = true
        payload.outboundSchedule = schedule
      }

      const res = await api.put(`/api/voice/numbers/${phone.id}/ai-mode`, payload)
      if (res.data.success) {
        await loadAIMode()
        if (direction === 'inbound') setEditingInbound(false)
        else setEditingOutbound(false)
        toast({ title: 'Schedule saved', description: 'AI hours updated' })
      }
    } catch (error: any) {
      toast({ title: 'Error', description: error.response?.data?.message || 'Failed to save schedule', variant: 'destructive' })
    } finally {
      setSaving(false)
    }
  }

  const saveForwardNumber = async () => {
    if (!forwardInput) return
    setSavingForward(true)
    try {
      await api.put(`/api/voice/numbers/${phone.id}/ai-mode`, {
        forwardNumber: phoneToE164(forwardInput),
      })
      setAiMode(prev => prev ? { ...prev, forwardNumber: phoneToE164(forwardInput) } : prev)
      setShowForwardConfig(false)
      toast({ title: 'Saved', description: 'Forwarding number updated' })
    } catch (error: any) {
      toast({ title: 'Error', description: error.response?.data?.message || 'Failed to save', variant: 'destructive' })
    } finally {
      setSavingForward(false)
    }
  }

  // State badge styling
  const stateBadge = (state: AIState) => {
    switch (state) {
      case 'on': return { label: 'ON', cls: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' }
      case 'off': return { label: 'OFF', cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' }
      case 'scheduled': return { label: 'SCHEDULED', cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' }
    }
  }

  const renderModeSelector = (direction: 'inbound' | 'outbound', currentState: AIState) => {
    const badge = stateBadge(currentState)
    const icon = direction === 'inbound'
      ? <PhoneIncoming className="h-3.5 w-3.5 text-brand-royal" />
      : <PhoneOutgoing className="h-3.5 w-3.5 text-brand-indigo" />
    const label = direction === 'inbound' ? 'AI Receptionist' : 'Lead Follow-Up'

    return (
      <div className="px-2 py-1.5 rounded-md bg-muted/40">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5">
            {icon}
            <span className="text-xs font-medium">{label}</span>
          </div>
          <div className="flex items-center gap-1">
            {saving && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
            <Select
              value={currentState}
              onValueChange={(val) => changeMode(direction, val as AIState)}
              disabled={saving || aiLoading}
            >
              <SelectTrigger className="h-6 w-auto min-w-0 border-0 bg-transparent p-0 gap-0.5 shadow-none focus:ring-0">
                <div className={`text-[10px] font-medium px-1.5 py-0.5 rounded transition-colors duration-300 ${badge.cls}`}>
                  {badge.label}
                </div>
              </SelectTrigger>
              <SelectContent align="end">
                <SelectItem value="on" className="text-xs">Always On</SelectItem>
                <SelectItem value="off" className="text-xs">Always Off</SelectItem>
                <SelectItem value="scheduled" className="text-xs">
                  <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> Scheduled</span>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Schedule summary when scheduled but not editing */}
        {currentState === 'scheduled' && direction === 'inbound' && !editingInbound && aiMode?.inboundSchedule && (
          <ScheduleSummary
            schedule={aiMode.inboundSchedule}
            isActiveNow={liveActive.inbound}
            onClick={() => {
              setDraftInboundSchedule(aiMode.inboundSchedule!)
              setEditingInbound(true)
            }}
          />
        )}
        {currentState === 'scheduled' && direction === 'outbound' && !editingOutbound && aiMode?.outboundSchedule && (
          <ScheduleSummary
            schedule={aiMode.outboundSchedule}
            isActiveNow={liveActive.outbound}
            onClick={() => {
              setDraftOutboundSchedule(aiMode.outboundSchedule!)
              setEditingOutbound(true)
            }}
          />
        )}
      </div>
    )
  }

  return (
    <div className="relative inline-flex flex-col items-center">
      <Card className="relative overflow-visible border-0 shadow-lg min-w-[280px] max-w-[340px]">
        {/* Gradient border glow — shifts color based on AI state */}
        <div className={`absolute -inset-px rounded-xl transition-colors duration-500 ${
          inboundActive && outboundActive
            ? 'bg-gradient-to-br from-brand-indigo to-brand-royal opacity-40'
            : !inboundActive && !outboundActive
              ? 'bg-gradient-to-br from-amber-400 to-orange-500 opacity-50'
              : 'bg-gradient-to-br from-brand-indigo to-amber-400 opacity-40'
        }`} />
        <div className="relative rounded-xl bg-card overflow-visible">
          <CardContent className="p-4">
            {/* Phone icon + number */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-brand-indigo/10 to-brand-royal/10 flex items-center justify-center">
                  <Phone className="h-4 w-4 text-brand-royal" />
                </div>
                <div>
                  <p className="font-semibold text-sm tabular-nums">{formatPhoneDisplay(phone.phoneNumber)}</p>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <span className={`inline-flex items-center px-1.5 py-px rounded text-[10px] font-medium ${
                      phone.provider === 'telnyx'
                        ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                        : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                    }`}>
                      {providerLabel}
                    </span>
                    <span className={`w-1.5 h-1.5 rounded-full ${
                      isActive ? 'bg-green-500' : phone.status === 'pending' ? 'bg-yellow-500' : 'bg-gray-400'
                    }`} />
                    <span className="text-[10px] text-muted-foreground capitalize">{phone.status}</span>
                  </div>
                </div>
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground">
                    <MoreVertical className="h-3.5 w-3.5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    className="text-destructive focus:text-destructive"
                    onClick={() => onDelete(phone.id, phone.phoneNumber)}
                  >
                    Delete Number
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* AI Mode Controls */}
            {isActive && aiMode && (
              <div className="space-y-2 mb-3">
                {/* Inbound mode selector */}
                {renderModeSelector('inbound', inboundState)}

                {/* Inbound schedule editor */}
                {editingInbound && (
                  <div className="rounded-md border border-blue-200/60 dark:border-blue-800/40 bg-blue-50/50 dark:bg-blue-950/20 p-2.5">
                    <ScheduleEditor
                      schedule={draftInboundSchedule}
                      onChange={setDraftInboundSchedule}
                      onSave={() => saveSchedule('inbound')}
                      onCancel={() => setEditingInbound(false)}
                      saving={saving}
                    />
                  </div>
                )}

                {/* Outbound mode selector */}
                {renderModeSelector('outbound', outboundState)}

                {/* Outbound schedule editor */}
                {editingOutbound && (
                  <div className="rounded-md border border-blue-200/60 dark:border-blue-800/40 bg-blue-50/50 dark:bg-blue-950/20 p-2.5">
                    <ScheduleEditor
                      schedule={draftOutboundSchedule}
                      onChange={setDraftOutboundSchedule}
                      onSave={() => saveSchedule('outbound')}
                      onCancel={() => setEditingOutbound(false)}
                      saving={saving}
                    />
                  </div>
                )}

                {/* Animated forwarding flow — shows when inbound AI is OFF or
                    Scheduled, OR when the user is being prompted to set a
                    forward number (showForwardConfig). The latter handles the
                    chicken-and-egg case where switching to Off requires a
                    forward number, but the input was previously gated on
                    already-being in Off/Scheduled state. */}
                {(inboundState === 'off' || inboundState === 'scheduled' || showForwardConfig) && (
                  <div className="relative overflow-hidden rounded-md bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/20 dark:to-orange-950/20 border border-amber-200/60 dark:border-amber-800/40 px-2.5 py-2">
                    <div className="flex items-center gap-2">
                      <PhoneIncoming className="h-3.5 w-3.5 text-amber-600 flex-shrink-0" />
                      {/* Animated dots flowing right */}
                      <div className="flex-1 relative h-3 flex items-center">
                        <div className="absolute inset-0 flex items-center">
                          <div className="w-full h-px bg-amber-300/60 dark:bg-amber-700/40" />
                        </div>
                        <div className="absolute inset-0 flex items-center overflow-hidden">
                          <div className="flex gap-1 animate-flow-right">
                            <div className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                            <div className="w-1.5 h-1.5 rounded-full bg-amber-400/70" />
                            <div className="w-1.5 h-1.5 rounded-full bg-amber-400/40" />
                          </div>
                        </div>
                      </div>
                      <PhoneForwarded className="h-3.5 w-3.5 text-amber-600 flex-shrink-0" />
                    </div>
                    <p className="text-[10px] text-amber-600 dark:text-amber-400 mt-1">
                      {inboundState === 'scheduled' ? 'Outside scheduled hours →' : 'All calls →'}{' '}
                      {hasForwardNumber ? (
                        <button
                          onClick={() => setShowForwardConfig(!showForwardConfig)}
                          className="font-medium hover:underline cursor-pointer"
                        >
                          {formatPhoneDisplay(aiMode.forwardNumber!)}
                        </button>
                      ) : (
                        <button onClick={() => setShowForwardConfig(true)} className="hover:underline cursor-pointer">
                          Set forwarding number...
                        </button>
                      )}
                    </p>

                    {/* Inline forward number editor */}
                    {showForwardConfig && (
                      <div className="mt-2 flex gap-1.5">
                        <Input
                          type="tel"
                          placeholder="+1 (555) 123 4567"
                          value={forwardInput}
                          onChange={(e) => setForwardInput(formatPhoneInput(e.target.value))}
                          className="h-7 text-xs flex-1 bg-white dark:bg-background"
                        />
                        <Button
                          size="sm"
                          className="h-7 px-2.5 text-xs"
                          onClick={saveForwardNumber}
                          disabled={!forwardInput || savingForward}
                        >
                          {savingForward ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Save'}
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {/* Outbound paused indicator */}
                {outboundState === 'off' && (
                  <div className="rounded-md bg-muted/60 px-2.5 py-1.5">
                    <p className="text-[11px] text-muted-foreground">
                      Form fills will be logged but calls won&apos;t be placed automatically.
                    </p>
                  </div>
                )}
                {outboundState === 'scheduled' && !editingOutbound && (
                  <div className="rounded-md bg-muted/60 px-2.5 py-1.5">
                    <p className="text-[11px] text-muted-foreground">
                      Outside scheduled hours, form fills are logged but calls won&apos;t fire.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Loading state for AI mode */}
            {isActive && !aiMode && aiLoading && (
              <div className="flex items-center justify-center py-2 mb-3">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            )}

            {/* Port indicators */}
            <div className="flex items-center justify-between gap-2">
              {/* Inbound port */}
              <Popover>
                <PopoverTrigger asChild>
                  <button className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md hover:bg-muted/80 transition-colors group/port flex-1 min-w-0">
                    <PhoneIncoming className="h-3.5 w-3.5 text-brand-royal flex-shrink-0" />
                    <span className="text-muted-foreground truncate">
                      {assignedInboundName || 'Unassigned'}
                    </span>
                    {!assignedInboundName && (
                      <Plus className="h-2.5 w-2.5 text-muted-foreground/60 group-hover/port:text-brand-royal flex-shrink-0" />
                    )}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-56 p-3" side="bottom" align="start">
                  <p className="text-xs font-medium mb-2">Inbound Agent</p>
                  <Select
                    value={phone.assistantId || 'none'}
                    onValueChange={(val) => onInboundChange(phone.id, val)}
                    disabled={assigningInbound}
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue placeholder="Select agent" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">
                        <span className="text-muted-foreground">None</span>
                      </SelectItem>
                      {inboundAgents.map((a) => (
                        <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </PopoverContent>
              </Popover>

              <div className="w-px h-6 bg-border/40 flex-shrink-0" />

              {/* Outbound port */}
              <Popover>
                <PopoverTrigger asChild>
                  <button className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md hover:bg-muted/80 transition-colors group/port flex-1 min-w-0">
                    <PhoneOutgoing className="h-3.5 w-3.5 text-brand-indigo flex-shrink-0" />
                    <span className="text-muted-foreground truncate">
                      {assignedOutboundName || 'Unassigned'}
                    </span>
                    {!assignedOutboundName && (
                      <Plus className="h-2.5 w-2.5 text-muted-foreground/60 group-hover/port:text-brand-indigo flex-shrink-0" />
                    )}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-56 p-3" side="bottom" align="end">
                  <p className="text-xs font-medium mb-2">Outbound Agent</p>
                  <Select
                    value={phone.outboundAssistantId || 'none'}
                    onValueChange={(val) => onOutboundChange(phone.id, val)}
                    disabled={assigningOutbound}
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue placeholder="Select agent" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">
                        <span className="text-muted-foreground">None</span>
                      </SelectItem>
                      {outboundAgents.map((a) => (
                        <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </PopoverContent>
              </Popover>
            </div>

            {/* Mobile: show agent names inline */}
            <div className="lg:hidden mt-2 space-y-1">
              {assignedInboundName && (
                <p className="text-[10px] text-muted-foreground">
                  Inbound: <span className="font-medium text-foreground/80">{assignedInboundName}</span>
                </p>
              )}
              {assignedOutboundName && (
                <p className="text-[10px] text-muted-foreground">
                  Outbound: <span className="font-medium text-foreground/80">{assignedOutboundName}</span>
                </p>
              )}
            </div>
          </CardContent>
        </div>
      </Card>
    </div>
  )
}
